import type { SessionUser } from '@accounts/shared';
import { type CSSProperties, type ReactNode, type SVGProps, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { STR } from '../i18n';
import { relativeTime } from '../lib/format';
import { type BellNotification, useBell } from '../lib/notifications';

/**
 * THE BELL — copied from the consumer apps' bell (BundleUp `web/src/components/
 * NotificationsBell.tsx`, Phase 17) so the suite has one bell, as the board asked: an
 * unread-count badge, a panel that opens on click, rows with unread dots, a click that
 * marks the row read and opens what it is about, "Mark all read", and close on an outside
 * click or Escape. Restyled with this app's tokens and classes; the structure is BundleUp's.
 *
 * The data is the bell table's (spec row 14): `lib/notifications.ts` reads the person's own
 * rows, the server's unread count and its wording, and marks rows read on the server. The
 * wave 1 stand-in it replaced derived the rows in the browser; this component kept its shape.
 *
 * TWO PLACEMENTS. `rail`: the admin rail's bottom group, directly above the theme control
 * (board, 18 Oct 23:04Z: bell, palette, mode, avatar) — a 30px square that fits the 96px
 * rail, its panel opening to the right. `launcher`: beside the account button, its panel
 * opening below. The panel is `position: fixed`, anchored to the button when it opens: the
 * rail is its own scroll container, and an absolutely placed panel inside it would be
 * clipped at the rail's edge.
 */

const PANEL_WIDTH = 340;
const PANEL_GAP = 8;

type Anchor = { left: number; bottom: number } | { right: number; top: number };

export function NotificationsBell({
  user,
  placement,
}: {
  user: SessionUser;
  placement: 'rail' | 'launcher';
}) {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const open = anchor !== null;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  const feed = useBell(user);

  const rows = feed.notifications;
  // The server's count: it covers every unread row, not only the 30 the panel lists.
  const count = feed.unreadCount;

  // Outside-click + Escape close (BundleUp's own), torn down whenever the panel is shut.
  useEffect(() => {
    if (!open) return;
    function onDocument(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (wrapRef.current !== null && !wrapRef.current.contains(target)) setAnchor(null);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setAnchor(null);
      buttonRef.current?.focus({ preventScroll: true });
    }
    function onResize() {
      setAnchor(null);
    }
    document.addEventListener('mousedown', onDocument);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDocument);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  function toggle() {
    if (open) {
      setAnchor(null);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    setAnchor(
      placement === 'rail'
        ? { left: rect.right + PANEL_GAP, bottom: Math.max(PANEL_GAP, window.innerHeight - rect.bottom) }
        : { right: Math.max(PANEL_GAP, window.innerWidth - rect.right), top: rect.bottom + 6 },
    );
  }

  function openRow(row: BellNotification) {
    feed.markRead(row);
    setAnchor(null);
    void navigate(row.link);
  }

  const label = count > 0 ? STR.access.bell.bellUnread(count) : STR.access.bell.bell;
  const badge = count > 99 ? STR.access.bell.overflow : String(count);

  const buttonStyle: CSSProperties =
    placement === 'rail'
      ? {
          position: 'relative',
          width: 30,
          height: 30,
          borderRadius: 9,
          display: 'grid',
          placeItems: 'center',
          padding: 0,
          cursor: 'pointer',
        }
      : {
          position: 'relative',
          width: 36,
          height: 36,
          borderRadius: 10,
          display: 'grid',
          placeItems: 'center',
          padding: 0,
          cursor: 'pointer',
        };

  let list: ReactNode;
  if (feed.isLoading && rows.length === 0) {
    list = <PanelNote>{STR.common.loading}</PanelNote>;
  } else if (feed.isError && rows.length === 0) {
    list = <PanelNote>{STR.errors.couldNotLoad}</PanelNote>;
  } else if (rows.length === 0) {
    list = <PanelNote>{STR.access.bell.empty}</PanelNote>;
  } else {
    list = rows.map((row) => {
      const isRead = row.read;
      return (
        <button
          key={row.key}
          type="button"
          className="menu-it"
          onClick={() => {
            openRow(row);
          }}
          style={{
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 2,
            borderRadius: 0,
            borderBottom: '1px solid var(--border)',
            padding: '9px 12px',
            ...(isRead ? {} : { background: 'var(--surface-2)' }),
          }}
        >
          <span style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
            <span
              className={isRead ? 'tx-muted' : undefined}
              style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600 }}
            >
              {row.title}
            </span>
            {isRead ? null : (
              <span
                role="img"
                aria-label={STR.access.bell.unread}
                style={{
                  width: 7,
                  height: 7,
                  marginTop: 5,
                  borderRadius: 999,
                  background: 'var(--accent)',
                  flex: 'none',
                }}
              />
            )}
          </span>
          <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
            {row.body}
          </span>
          <span className="tx-subtle" style={{ fontSize: 10.5, fontWeight: 500 }}>
            {relativeTime(row.at)}
          </span>
        </button>
      );
    });
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: 'none' }}>
      <button
        ref={buttonRef}
        type="button"
        className="ghost"
        aria-label={label}
        title={label}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={toggle}
        style={buttonStyle}
      >
        <BellIcon size={placement === 'rail' ? 15 : 17} aria-hidden="true" />
        {count > 0 ? (
          // The danger pair `.dbtn` already uses: danger-strong under the surface colour.
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -5,
              right: -6,
              minWidth: 17,
              height: 17,
              padding: '0 4px',
              boxSizing: 'border-box',
              borderRadius: 999,
              border: '2px solid var(--surface)',
              background: 'var(--bg-danger-strong)',
              color: 'var(--surface)',
              fontSize: 9.5,
              fontWeight: 700,
              lineHeight: '13px',
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {badge}
          </span>
        ) : null}
      </button>

      {anchor === null ? null : (
        <div
          role="dialog"
          aria-label={STR.access.bell.title}
          style={{
            position: 'fixed',
            ...anchor,
            zIndex: 70,
            width: `min(${String(PANEL_WIDTH)}px, calc(100vw - ${String(PANEL_GAP * 2)}px))`,
            maxHeight: 480,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface)',
            border: '1px solid var(--border-strong)',
            borderRadius: 12,
            boxShadow: 'var(--elev-pop)',
            overflow: 'hidden',
            color: 'var(--text)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '8px 10px 8px 12px',
              borderBottom: '1px solid var(--border)',
              flex: 'none',
            }}
          >
            <span style={{ fontSize: 12.5, fontWeight: 650 }}>{STR.access.bell.title}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {count > 0 ? (
                <button
                  type="button"
                  className="tbtn"
                  onClick={() => {
                    feed.markAllRead();
                  }}
                  style={{ padding: '3px 7px', borderRadius: 6, fontSize: 11.5, fontWeight: 600 }}
                >
                  {STR.access.bell.markAllRead}
                </button>
              ) : null}
              <button
                type="button"
                className="tbtn"
                aria-label={STR.access.bell.close}
                title={STR.access.bell.close}
                onClick={() => {
                  setAnchor(null);
                  buttonRef.current?.focus({ preventScroll: true });
                }}
                style={{ padding: 4, borderRadius: 6, display: 'grid', placeItems: 'center' }}
              >
                <CloseIcon size={12} aria-hidden="true" />
              </button>
            </span>
          </div>
          <div style={{ overflowY: 'auto', minHeight: 0 }}>{list}</div>
        </div>
      )}
    </div>
  );
}

function PanelNote({ children }: { children: ReactNode }) {
  return (
    <div className="tx-subtle" style={{ padding: '28px 12px', textAlign: 'center', fontSize: 12 }}>
      {children}
    </div>
  );
}

/* BundleUp's icons (Lucide bell and X, `components/icons.tsx`), same stroke and sizing. */
type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Svg({ size = 24, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </svg>
  );
}

function BellIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </Svg>
  );
}

function CloseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Svg>
  );
}
